import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { DateInput, DateInputProps, DateRangePicker, SelectionRange } from "@progress/kendo-react-dateinputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { ExcelExport, ExcelExportColumn as ExportColumn } from "@progress/kendo-react-excel-export";
import { GridColumn as Column, Grid, GridToolbar } from '@progress/kendo-react-grid';
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { arrowRotateCwIcon, downloadIcon, filterClearIcon } from "@progress/kendo-svg-icons";
import { TurnoverNoteViewModel } from "TypeGen/ShiftTurnover/turnover-note-view-model";
import { ViewModel } from "TypeGen/ShiftTurnover/view-model";
import { ApiLink } from "TypeGen/api-link";
import { IDNameViewModel } from "TypeGen/id-name-view-model";
import DateCell from "components/cells/DateCell";
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchApi } from "services/api";
import { useDebouncedCallback } from 'use-debounce';
import { Title } from "utils/title";
import { dateFormatter } from "utils/utils";
import TripSummary from "views/Track/TripSummary";
import { openWindowPerc } from "../../services/openWindow";

const DateRangeStartDate = dayjs().subtract(2, "days").startOf('day').toDate();
const DateRangeEndDate = dayjs().add(1, "days").startOf('day').toDate();

const dispatchTeams = [
  { ID: '', Name: 'All dispatch teams' },
  { ID: 'DTW', Name: 'DTW' },
  { ID: 'CLT', Name: 'Charlotte' },
  { ID: 'DAY', Name: 'Dayton' },
  { ID: 'FWA', Name: 'Fort Wayne' },
  { ID: 'GR', Name: 'Grand Rapids' },
  { ID: 'TN', Name: 'Tennessee' },
  { ID: 'MX', Name: 'Mexico' },
  { ID: 'TNMX', Name: 'Tennessee MX' },
  { ID: 'CLTMX', Name: 'Charlotte MX' },
  { ID: 'TOL', Name: 'Toledo' },
];

const CustomDateInput = (props: DateInputProps) => {
  return (
    <DateInput {...props} label={undefined} width={92} formatPlaceholder={{ year: 'yyyy', month: 'MM', day: 'dd' }} />
  );
};

enum NoteType {
  General = 1,
  Billing = 2,
}

enum StatusFilter {
  Unacknowledged = "UNACKNOWLEDGED",
  Acknowledged = "ACKNOWLEDGED",
  Resolved = "RESOLVED",
}

const ShiftTurnover = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const [orderNumber, setOrderNumber] = useState(() => {
    const param = urlParams.get('ordernumber');
    return param && !isNaN(Number(param)) ? Number(param) : null;
  });
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState<NoteType>(NoteType.General);
  const [notes, setNotes] = useState<TurnoverNoteViewModel[]>([]);
  const [serviceTeams, setServiceTeams] = useState<IDNameViewModel[]>([]);
  const [showTripSummary, setShowTripSummary] = useState(0);
  const _excelExport = useRef<ExcelExport | null>(null);

  // Filters
  const [filterByDateRange, setFilterByDateRange] = useState<SelectionRange>({
    start: DateRangeStartDate,
    end: DateRangeEndDate,
  });
  const [statusFilter, setStatusFilter] = useState<StatusFilter[]>([StatusFilter.Unacknowledged]);
  const [filterMine, setFilterMine] = useState<boolean>(false);
  const [filterServiceTeams, setFilterServiceTeams] = useState<number[]>([]);
  const [filterDispatchTeam, setFilterDispatchTeam] = useState('');

  const fetchData = useCallback(() => {
    if (!filterByDateRange || !filterByDateRange.start || !filterByDateRange.end) {
      return;
    }

    setLoading(true);
    fetchApi('/api/Order/ShiftTurnover', {
      FromDate: filterByDateRange.start,
      ToDate: filterByDateRange.end,
      Type: type,
      IncludeUnacknowledged: statusFilter.includes(StatusFilter.Unacknowledged),
      IncludeAcknowledged: statusFilter.includes(StatusFilter.Acknowledged),
      IncludeResolved: statusFilter.includes(StatusFilter.Resolved),
      Mine: filterMine,
      ServiceTeamID: filterServiceTeams,
      DispatchTeam: filterDispatchTeam || null,
      OrderNumber: orderNumber
    }, 'POST')
      .then((response: ViewModel) => {
        setNotes(response.Notes);
        setServiceTeams(response.ServiceTeams);
      })
      .finally(() => setLoading(false));
  }, [filterByDateRange, type, statusFilter, filterMine, filterServiceTeams, filterDispatchTeam, orderNumber]);

  const debouncedFetchRecords = useDebouncedCallback(() => {
    fetchData();
  }, 750);

  const reset = () => {
    setFilterByDateRange({
      start: DateRangeStartDate,
      end: DateRangeEndDate,
    });
    setStatusFilter([StatusFilter.Unacknowledged]);
    setFilterMine(false);
    setFilterServiceTeams([]);
    setFilterDispatchTeam('');
    setOrderNumber(null);
    setType(NoteType.General);
    debouncedFetchRecords();
  }

  const mark = (link: ApiLink) => {
    fetchApi(link.Link, {}, link.Method)
      .then(() => fetchData());
  }

  const excelExport = () => {
    const formattedNotes = notes.map((item) => ({
      ...item,
      ModifiedDateTime: dateFormatter(item.ModifiedDateTime),
      AcknowledgedDateTime: item.AcknowledgedDateTime ? dateFormatter(item.AcknowledgedDateTime) : '',
      ResolvedDateTime: item.ResolvedDateTime ? dateFormatter(item.ResolvedDateTime) : '',
    }));
    _excelExport.current?.save(formattedNotes);
  };

  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      fetchData();
      return;
    }

    debouncedFetchRecords();
  
  }, [filterByDateRange, type, statusFilter, filterMine, filterServiceTeams, filterDispatchTeam, orderNumber, debouncedFetchRecords, fetchData]);

  const toggleStatusFilter = (status: StatusFilter) => {
    setStatusFilter((current) => {
      if (current.includes(status)) {        
        if (current.length === 1) {
          return current;
        }

        return current.filter((x) => x !== status);
      }

      return [...current, status];
    });
  };

  return <>
    <Title string="Shift Turnover" />
    <br />
    <ExcelExport
      ref={_excelExport}
      data={notes}
      fileName={`Shift Turnover ${type === NoteType.Billing ? "Billing" : "General"} Notes.xlsx`}
    >
      <ExportColumn field="OrderNumber" title="Order #" width={70} />
      <ExportColumn field="BookedByFullName" title="Booked By" />
      <ExportColumn field="Note" width={300} />
      <ExportColumn field="ModifiedByFullName" title="Created By" />
      <ExportColumn field="ModifiedDateTime" title="Created" />
      <ExportColumn field="AcknowledgedByFullName" title="Acknowledged By" />
      <ExportColumn field="AcknowledgedDateTime" title="Acknowledged" />
      <ExportColumn field="ResolvedByFullName" title="Resolved By" />
      <ExportColumn field="ResolvedDateTime" title="Resolved" />
    </ExcelExport>
    <Grid data={notes} showLoader={loading} dataItemKey="OrderNoteID">
      <GridToolbar overflow="none">
        <Button
          title="Reset Sort"
          icon="filter-clear"
          svgIcon={filterClearIcon}
          onClick={reset}
        />
        <Button
          title="Refresh"
          icon="refresh"
          svgIcon={arrowRotateCwIcon}
          onClick={fetchData}
        />
        <Button svgIcon={downloadIcon} onClick={excelExport} />
        <NumericTextBox
          value={orderNumber}
          placeholder='Order Number'
          min={0}
          spinners={false}
          width={130}
          format="0"
          onChange={(e) => setOrderNumber(e?.value)}
        />
        <ButtonGroup>
          <Button togglable
            selected={type === NoteType.General}
            onClick={() => setType(NoteType.General)}
          >
            General
          </Button>
          <Button togglable
            selected={type === NoteType.Billing}
            onClick={() => setType(NoteType.Billing)}
          >
            Billing
          </Button>
        </ButtonGroup>
        <ButtonGroup>
          <Button
            togglable={true}
            selected={statusFilter.includes(StatusFilter.Unacknowledged)}
            onClick={() => toggleStatusFilter(StatusFilter.Unacknowledged)}
          >
            Unacknowledged
          </Button>
          <Button
            togglable={true}
            selected={statusFilter.includes(StatusFilter.Acknowledged)}
            onClick={() => toggleStatusFilter(StatusFilter.Acknowledged)}
          >
            Acknowledged
          </Button>
          <Button
            togglable={true}
            selected={statusFilter.includes(StatusFilter.Resolved)}
            onClick={() => toggleStatusFilter(StatusFilter.Resolved)}
          >
            Resolved
          </Button>
        </ButtonGroup>
        <Button
          togglable={true}
          selected={filterMine}
          onClick={() => setFilterMine(!filterMine)}
        >
          Show My Activity
        </Button>
        <DateRangePicker
          style={{ width: 195, gap: "0.3em" }}
          startDateInput={CustomDateInput}
          endDateInput={CustomDateInput}
          value={filterByDateRange}
          onChange={(e) => {
            setFilterByDateRange(e.value);
            if (e.value && e.value.start && e.value.end) {
              fetchData();
            }
          }}
        />
        <ButtonGroup>
          {serviceTeams.map((teamId) => <Button
            key={teamId.ID}
            togglable={true}
            selected={filterServiceTeams.includes(teamId.ID)}
            onClick={() => {
              if (filterServiceTeams.includes(teamId.ID)) {
                setFilterServiceTeams(filterServiceTeams.filter((id) => id !== teamId.ID));
              } else {
                setFilterServiceTeams([...filterServiceTeams, teamId.ID]);
              }
            }}
          >
            {teamId.Name}
          </Button>)}
        </ButtonGroup>
        <DropDownList
          ariaLabel="Dispatch team"
          data={dispatchTeams}
          dataItemKey="ID"
          textField="Name"
          value={dispatchTeams.find((team) => team.ID === filterDispatchTeam)}
          onChange={(e) => setFilterDispatchTeam(e.value.ID)}
          style={{ width: 180 }}
        />
      </GridToolbar>
      <Column
        field="OrderNumber"
        title="Order #"
        cells={{
          data: (props) => <td {...props.tdProps}>
            <a
              href="#"
              style={{ color: "#007bff" }}
              onClick={(e) => {
                e.preventDefault();
                openWindowPerc(`/Order/${props.dataItem.OrderID}`);
              }}
            >
              {props.dataItem.OrderNumber}
            </a>
          </td>
        }}
      />
      <Column
        field="BookedByFullName"
        title="Booked By"
        cells={{
          data: (props) => <td {...props.tdProps}>
            <a
              href="#"
              style={{ color: "#007bff" }}
              onClick={(e) => {
                e.preventDefault();
                setShowTripSummary(props.dataItem.OrderID);
              }}
            >
              {props.dataItem.BookedByFullName}
            </a>
          </td>
        }}
      />      
      <Column field="Note" width={300} />
      <Column field="ModifiedByFullName" title="Created By" />
      <Column field="ModifiedDateTime" title="Created" cells={{ data: DateCell }} />
      <Column field="AcknowledgedByFullName" title="Acknowledged By" />
      <Column field="AcknowledgedDateTime" title="Acknowledged" cells={{ data: DateCell }} />
      <Column field="ResolvedByFullName" title="Resolved By" />
      <Column field="ResolvedDateTime" title="Resolved" cells={{ data: DateCell }} />
      <Column title="Actions" width={140} cells={{ data: (props) => {
        const acknowledge = props.dataItem.Links.find((x: ApiLink) => x.Name === "Acknowledge");
        const unacknowledge = props.dataItem.Links.find((x: ApiLink) => x.Name === "Unacknowledge");
        const resolve = props.dataItem.Links.find((x: ApiLink) => x.Name === "Resolve");
        return <td {...props.tdProps}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {acknowledge && <Button size="small" themeColor="success" title="Acknowledge" onClick={() => mark(acknowledge)}>Acknowledge</Button>}
            {unacknowledge && <Button size="small" themeColor="error" title="Unacknowledge" onClick={() => mark(unacknowledge)}>Unacknowledge</Button>}
            {resolve && <Button size="small" themeColor="primary" title="Resolve" onClick={() => mark(resolve)}>Resolve</Button>}
          </div>
        </td>;
      } }} />
    </Grid>
    {showTripSummary > 0 && <TripSummary orderId={showTripSummary} close={() => setShowTripSummary(0)} />}
  </>;
}

export default ShiftTurnover;
